import * as THREE from 'three';
import gsap from 'gsap';

import vertex from './shaders/vertex';
import fragment from './shaders/fragment';
import postProcessVertex from './shaders/postProcessVertex';
import postProcessFragment from './shaders/postProcessFragment';
import { createAdaptiveResolution } from './quality';
import { POST_PROCESS } from './postProcessConfig';
import { SECTION_TRANSITIONS, SECTION_TRANSITION_DEFAULT } from './transitionConfig';
import { MANIFESTO_LOOK, MANIFESTO_EXPLODE, MANIFESTO_COLLAPSE, HOLD_RELEASE, SHAKE, MANIFESTO_TIMESPEED_AUDIO_GAIN } from './manifestoConfig';
import { getManifestoVoiceLevel } from '../../../audio/manifestoVoice';
import { getLabState } from '../../../lib/labBackground';

/**
 * ShimmeringPrism — vanilla Three.js raymarched background engine.
 *
 * Ported from the standalone demo (src/newbackground/js/main.js) into an
 * instance-based class so it can mount/unmount cleanly inside React: one
 * instance per mount, full WebGL disposal on unmount, no module-level globals
 * that a second mount (or HMR) could clobber.
 *
 * Differences from the standalone, by integration requirement:
 *   - No microphone / audio input / AudioWorklet (already removed upstream).
 *     The old `audio.volume` term is folded into the default `timeSpeed`.
 *   - No standalone text overlay or console branding.
 *   - GLSL is byte-identical to the standalone (see shaders/).
 *
 * Reached parity with the standalone (these were previously deferred):
 *   - Mouse-orientation parallax: the camera eases a small extra rotation toward
 *     the cursor, layered on the slow autonomous spin (live for all visitors).
 *   - prismSize uniform: the central white octahedron is tunable.
 *   - ?debug panel: lazy-mounted by ShimmeringBackground when the URL has `?debug`,
 *     mutating this engine's public `this.params` live.
 *   - setSection(index): per-section presets with scroll-driven, inertial transition choreography
 *     (see transitionConfig.js) — Hero→Projects ramps the spin up / opens / coasts down under friction;
 *     Lab→About is a weighted, eased camera glide; About→Hero (the hidden bottom-scroll loop) closes +
 *     recenters then coasts the spin down. ShimmeringBackground subscribes to homeSection and drives this.
 *   - Background post-process pass: level lift + chromatic aberration + film grain, composited
 *     through a render target (level→CA→noise order). Per-section, subtle; ?debug-tunable.
 *   - Manifesto ritual: setHold(0..1|null) freezes + shakes the form during the hero hold; on completion
 *     setManifestoActive(true) explodes to MANIFESTO_LOOK (central sphere) and back on deactivate. Driven
 *     from HomeHero/Home via the manifestoBackground pub/sub (see manifestoConfig.js).
 *
 * TODO (later phases — clean hooks, intentionally not wired yet):
 *   - setIntensity(v): audio-amplitude reactivity (stored hook; mapping TBD).
 *   - Manifesto subtitles (timed cues against the voice).
 */

// Module-scope constant (read-only, shared safely).
const CAMERA_UP = new THREE.Vector3(0.0, 1.0, 0.0);

// Default light tint: white = neutral (a multiplicative no-op in the shader), so the form looks exactly
// as before until a PROJECTS item is shown. setLightColor() moves it toward the hovered project's color.
const DEFAULT_LIGHT_COLOR = 0xffffff;

// LAB drag → background rotation: reject a single-frame carousel-offset change larger than this (item
// units). Real drag/coast moves < ~0.2 item/frame; a bigger jump is a filter reset / programmatic
// re-baseline and must NOT spin the form. (See _animate's _labOrbit accumulation.)
const LAB_DRAG_MAX_STEP = 0.5;

const TWO_PI = Math.PI * 2.0; // wrap bound for the integrated burst-spin phase (cos/sin periodic)

const INTRO_DURATION = 2.0; // seconds — shininess fades 0 -> params.shininess once on start

// Mobile profile calming. On phones/coarse-pointer devices the background sits behind touch-native
// content, so it should be dimmer and quieter than desktop. These multipliers are applied as READ-TIME
// scalars on the param→uniform writes (not by mutating SECTION_PRESETS or the tween targets), so the
// presets, the transition choreography, and the entire desktop path stay byte-identical — desktop never
// takes the mobile branch (isMobileProfile === false). mouseRotAmp is forced to 0 separately at its read
// sites (no cursor on touch). See _mob() and ShimmeringBackground.getMobileBackgroundProfile().
const MOBILE_PARAM_MUL = {
    shininess: 0.55,             // dimmer/softer central glow (polish 2: was 0.65 — less harsh highlight)
    chromaticAberrationPx: 0.3,  // less radial RGB fringing (polish 2: was 0.45 — cleaner edges)
    filmNoiseAmount: 0.2,        // less film grain (polish 2: was 0.4 — quieter, less "dirty" mobile noise)
    shakeAmp: 0.5,               // calmer manifesto-hold tremble on phones (~50% — expressive, not violent)
    cameraScrollShift: 0.4,      // gentler vertical scroll pan
    cameraDist: 1.35,            // pull the camera back on mobile so the shape reads smaller /
                                 // less zoomed-cropped and doesn't compete with the foreground
};

// Mobile transition pacing: section look/scroll tweens run faster (shorter, snappier passages
// on touch) and the burst-spin peaks lower (calmer, no long coast). Both gated by isMobileProfile
// so desktop choreography is byte-identical.
const MOBILE_SECTION_TIMESCALE = 1.5; // 1s desktop tween → ~0.66s on mobile
const MOBILE_SPIN_SCALE = 0.6;        // scale peak burst-spin velocity down on mobile

// Per-section "character" presets. The look interpolates between these as the user scrolls the
// homepage (see setSection). Each is copied into the per-instance `this.params` so the ?debug panel
// can mutate values live without clobbering the presets or leaking between mounts.
//   home → hero / info / off-route resting look
//   deep → projects (denser fractal, pulled back, slower drift)
//   lab  → lab: the deep look, but the central prism collapses to 0 (derived from deep below)
// `home` doubles as the initial state (this.params is seeded from it). It reproduces the prior look
// except cameraDist, now 40 per the section spec (was 49.5); shininess 0.25 = old INTRO_SHININESS;
// timeSpeed 0.02 folds the old constant intensity*0.01 term in so timeSpeed = 0 truly pauses.
const SECTION_PRESETS = {
    home: {
        shininess: 0.25,            // glow / brightness target the intro fades up to
        baseOverture: 4,            // fractal shape
        prismSize: 0,               // size of the central white octahedron ("prism") — hidden on home
        overtureRandomness: 9.7364, // overture sine phase (dormant: overture is pinned to baseOverture)
        timeSpeed: 0.02,            // animation speed (0 = pause)
        cameraDist: 40,             // camera distance from the centre
        camRotSpeedX: 0.039,        // horizontal orbit speed
        camRotSpeedY: 0.003,        // vertical tilt speed
        mouseRotAmp: 0.25,          // mouse parallax strength (radians at the screen edge)
        mouseSmooth: 0.08,          // mouse parallax easing (lower = laggier)
        // Post-process (background only): currently constant across sections (being tuned).
        blackLift: 0.015,           // raise pure black slightly (0 = none)
        chromaticAberrationPx: 9,   // radial RGB separation at the edge, in pixels
        filmNoiseAmount: 0.05,      // legacy film-grain peak amplitude (0 = off)
        cameraScrollShift: 15,      // vertical pan per section scrolled (0 = off; see note below)
    },
    deep: {
        shininess: 0.35,
        baseOverture: 18.35,
        prismSize: 0,
        overtureRandomness: 9.7364,
        timeSpeed: 0.01,            // animation speed — drives PROJECTS and (inherited) LAB
        cameraDist: 85,
        camRotSpeedX: 0.03,
        camRotSpeedY: 0.003,
        mouseRotAmp: 0.25,
        mouseSmooth: 0.08,
        // Post-process: identical to home (constant across sections for now).
        blackLift: 0.015,
        chromaticAberrationPx: 9,
        filmNoiseAmount: 0.05,
        cameraScrollShift: 15,
    },
};

// LAB ("deeper into the nebula"): the deep/projects look, pushed further in. Derived from `deep` so it
// tracks any future deep retune, then overrides: prismSize 0 (no central solid), cameraDist 95 (pulled
// back from deep's 85 → the field recedes = depth), timeSpeed 0.008 (calmer drift than deep's 0.01). The
// PROJECTS→LAB scroll therefore reads as a gentle descent (the existing projectsToLab settle now expresses
// it) rather than a no-op; the UI is DOM, so only the background framing moves.
SECTION_PRESETS.lab = { ...SECTION_PRESETS.deep, prismSize: 0, cameraDist: 95, timeSpeed: 0.008 };

// ABOUT (info / section 3): the OPENED form, close-up and quiet — you are "inside" the opened system,
// not back at the closed Hero emblem. Derived from `deep` (so it inherits the opened baseOverture 18.35,
// NOT home's closed 4), then made calm and close: dimmer (shininess 0.09, so the close-up form never
// overpowers About typography), gently animated (timeSpeed 0.015), pulled very close (cameraDist 18, vs Lab's
// 85), no autonomous orbit (camRotSpeedX 0), scroll pan disabled (cameraScrollShift 0).
// Tuning note: cameraDist 18 is the immersion/readability knob — if the opened form clips or overpowers
// the text, raise it (≈28–35) live in ?debug, then here.
SECTION_PRESETS.about = {
    ...SECTION_PRESETS.deep,
    // ABOUT is the calm core / arrival point — the dimmest, steadiest, most legible section.
    shininess: 0.16,            // dimmer glow (was 0.2) so the close-up form never overpowers the CV text
    timeSpeed: 0.015,
    cameraDist: 18,
    camRotSpeedX: 0,
    mouseRotAmp: 0.12,          // subtler mouse parallax (vs deep's 0.25) → the background sits steady behind text
    cameraScrollShift: 0,
    // Calmer post-process behind the CV typography (the one section with sustained reading): less
    // chromatic fringing (9→5 px) and grain (0.05→0.03) than the rest of the site, so the text reads
    // cleaner. Crossfaded in by the existing section param tween; the post-process pass already reads
    // these per-frame. ABOUT becomes the quietest, most legible section without leaving the visual world.
    chromaticAberrationPx: 5,
    filmNoiseAmount: 0.03,
};

// Home section indices: 0 hero · 1 projects · 2 lab · 3 info; null = home route unmounted.
// Projects → "deep"; lab → "lab" (deep, prismSize 0); info → "about"; hero and off-route → "home".
function presetKeyForSection(section) {
    if (section === 1) return 'deep';
    if (section === 2) return 'lab';
    if (section === 3) return 'about';
    return 'home';
}

// Section-preset crossfade timing — matches Home.js's section scroll tween (the gsap window scrollTo).
const SECTION_TWEEN = { duration: 1, ease: 'power4.out' };

// Scroll-coupled vertical camera pan. As the user moves between home sections the camera eases
// vertically, in lockstep with the 1s section scroll (driven off the section INDEX rather than
// window.scrollY, since the home route uses overflow:hidden snap-scrolling, not document scroll).
// `cameraScrollShift` is a per-section preset param (above): world units of vertical pan per section
// index (0 hero … 3 info); 0 = off; positive moves the view "with" the scroll. Live-tunable in ?debug.
// It tweens with the section like the other params, so e.g. ABOUT eases its pan to 0. The applied
// offset is _scrollSection * params.cameraScrollShift (see _updateCameraUniforms).

export default class ShimmeringPrism {
    /**
     * @param {HTMLElement} container Fixed full-screen mount element.
     * @param {object} [options]
     * @param {boolean} [options.animate=true]  false => render one static frame (reduced motion).
     * @param {number}  [options.intensity=1.0] Stored audio/manifesto hook (see setIntensity); inert for now.
     * @param {number}  [options.quality=0.75]  Initial adaptive-resolution pixel ratio.
     */
    constructor(container, options = {}) {
        this.container = container;
        this.animateEnabled = options.animate !== false;
        this.intensity = typeof options.intensity === 'number' ? options.intensity : 1.0;
        this.initialQuality = typeof options.quality === 'number' ? options.quality : 0.75;
        // Adaptive-resolution range (mobile passes a lower, capped range). Defaults reproduce the
        // original hardcoded [0.5, 1.0] so desktop is unchanged.
        this.qualityMin = typeof options.qualityMin === 'number' ? options.qualityMin : 0.5;
        this.qualityMax = typeof options.qualityMax === 'number' ? options.qualityMax : 1.0;
        // Mobile profile flag: gates the read-time calming multipliers (MOBILE_PARAM_MUL) and
        // zeroes mouse parallax. false on desktop → every mobile branch is skipped (byte-identical).
        this.isMobileProfile = options.mobile === true;

        // Public, mutable look/feel parameters (the ?debug panel reads/writes these live). Seeded
        // from the home preset; setSection() crossfades them to the active section's preset.
        // uShape / uIcoComplexity are global knobs (not per-section): presets don't carry them, so
        // setSection()'s param tweens never overwrite them. uShape 2 = Sphere is now the chosen central
        // solid. prismSize stays 0 in all presets (central solid invisible, exactly as the prism worked),
        // so this only changes the central glow's falloff from octahedral to spherical. ?debug to audition.
        this.params = { ...SECTION_PRESETS.home, uShape: 2, uIcoComplexity: 3, shakeAmp: SHAKE.ampDefault, manifestoAudioSensitivity: 2.0, labDragRotation: -1.0 }; // includes cameraScrollShift (now a per-section param); labDragRotation = rad of bg azimuth per LAB carousel item (negative = rotates WITH the UI drag; magnitude = sensitivity)
        // Manifesto hold state. _motionScale 1→0 freezes all motion in place during the 2s hold;
        // _shake01 0→1 ramps the camera tremble; _holdGlow 0→1 ramps shininess toward 1.0 (the bulb
        // charging). All driven by setHold() from HomeHero's progress, released on abort, and reset by
        // setManifestoActive() on completion. See manifestoConfig.js.
        this._motionScale = 1.0;
        this._shake01 = 0.0;
        this._holdGlow = 0.0;
        this._manifestoActive = false;
        this._sectionKey = 'home';   // current preset key (avoids redundant re-tweens)
        this._lookTween = null;      // in-flight gsap tween/timeline of this.params (look choreography)
        this._scrollSection = 0;     // eased section index (0..3) driving the vertical scroll pan
        this._scrollTween = null;    // in-flight gsap tween of _scrollSection, if any
        this.activeTransition = null; // name of the running look choreography (null = none / settled);
                                       // truthy ⇒ "isSectionTransitioning" (inspectable, e.g. in ?debug)

        // Inertial spin model: an extra orbit angle (_spinPhase) integrated each frame from an angular
        // velocity (_spinVel, rad/s), layered ON TOP of the autonomous time*camRotSpeedX orbit. At rest
        // (_spinVel 0) _spinPhase holds steady, so the look is byte-identical to before. A transition's
        // GSAP tween ramps _spinVel UP to a peak (smooth S-curve, _spinAccel true while it runs); when
        // released (_spinAccel false) the velocity DECAYS each frame through friction (_spinVel *=
        // _spinDamping per 60fps frame) and is clamped to 0 only below _spinCutoff — so the spin coasts
        // down with inertia instead of being braked to 0 by a tween. No phase jump (integrating is
        // continuous); never a competing tween + frame write (damping is gated off while _spinAccel).
        this._spinPhase = 0;
        this._spinVel = 0;
        this._spinAccel = false;   // true while a GSAP tween is actively driving _spinVel (suspends decay)
        this._spinDamping = 0.94;  // per-60fps-frame friction (set per-transition; default = safe coast)
        this._spinCutoff = 0.002;  // rad/s — below this the coasting spin snaps to 0 (imperceptible)
        // Autonomous orbit angles, integrated as a RATE (camRotSpeed × Δtime) each frame rather than
        // computed as time × camRotSpeed. This means changing camRotSpeed only affects the orbit going
        // forward — tweening it to 0 (e.g. the manifesto) decelerates smoothly to a stop instead of
        // retroactively swinging the whole accumulated angle. Steady-state speed is identical.
        this._orbitX = 0;
        this._orbitY = 0;

        // LAB carousel → horizontal background rotation. _labOrbit is an extra azimuth offset accumulated
        // from the frame-to-frame delta of the published carousel offset (lib/labBackground.carouselOffset);
        // _prevLabOffset tracks the last sample. Added into the camera azimuth `a` in _updateCameraUniforms.
        this._labOrbit = 0;
        this._prevLabOffset = 0;

        // Central-glow tint, driven by the shown PROJECTS item (setLightColor). Persists across the whole
        // site; defaults to white (neutral). Copied into the lightColor uniform every frame.
        this._lightColor = new THREE.Color(DEFAULT_LIGHT_COLOR);

        // Post-process pass (background-only level lift + chromatic aberration + film grain).
        this.postEnabled = POST_PROCESS.enabled !== false;
        // Real-seconds accumulator driving the grain animation. Seeded > 0 because the legacy grain
        // pattern degenerates to a flat shift at time 0 (matters for the reduced-motion static frame).
        this._postElapsed = 1.0;
        this._manifestoTween = null; // in-flight gsap tween of the post params (manifesto hook)

        this.time = 0;
        this.last = 0;
        this.introElapsed = 0.0;
        this.rafId = null;
        this.disposed = false;
        this.started = false;

        // Mouse-parallax state: the camera eases a small extra rotation toward the mouse
        // (strength params.mouseRotAmp, easing params.mouseSmooth), layered on the slow spin.
        this.mouseTargetX = 0; this.mouseTargetY = 0; // normalized [-1,1]; 0 = centre / no offset
        this.mouseX = 0; this.mouseY = 0;             // eased values actually used by the camera

        // Scratch vectors for the per-frame camera basis (avoid per-frame allocation).
        this._ro = new THREE.Vector3();
        this._ww = new THREE.Vector3();
        this._uu = new THREE.Vector3();
        this._vv = new THREE.Vector3();

        this._animate = this._animate.bind(this);
        this._onResize = this._onResize.bind(this);
        this._onMouseMove = this._onMouseMove.bind(this);

        this._init();
    }

    _init() {
        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);

        // antialias is useless here: a fullscreen raymarch quad has no interior geometry
        // edges for MSAA to sample, and MSAA can't fix shader-content aliasing.
        this.renderer = new THREE.WebGLRenderer({ antialias: false });
        this.container.appendChild(this.renderer.domElement);

        this.scene = new THREE.Scene();

        this.uniforms = {
            resolution: { value: new THREE.Vector2() }, // populated by quality.apply() below
            time: { value: 0.0 },
            shininess: { value: 0.0 },
            overture: { value: this.params.baseOverture },
            prismSize: { value: this.params.prismSize },
            // Central-solid shape selector (0 Prism · 1 Cube · 2 Sphere · 3 Icosahedron) + ico facet
            // richness. Floats (not ints) to avoid GLSL ES 1.00 / Three.js int-uniform pitfalls.
            uShape: { value: this.params.uShape },
            uIcoComplexity: { value: this.params.uIcoComplexity },
            // Camera origin + basis, recomputed on the CPU each frame (frame-constant per pixel).
            uRo: { value: new THREE.Vector3() },
            uUu: { value: new THREE.Vector3() },
            uVv: { value: new THREE.Vector3() },
            uWw: { value: new THREE.Vector3() },
            // Per-project glow tint (white = neutral). Driven by setLightColor from the PROJECTS hover.
            lightColor: { value: new THREE.Color(DEFAULT_LIGHT_COLOR) },
        };

        this.material = new THREE.RawShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: vertex,
            fragmentShader: fragment,
        });

        this.geometry = new THREE.PlaneBufferGeometry(2, 2);
        this.mesh = new THREE.Mesh(this.geometry, this.material);
        this.scene.add(this.mesh);

        // Adaptive resolution owns the pixel ratio + the `resolution` uniform; sets them now.
        // (Run before _initPostProcess so the drawing-buffer size is valid for the render target.)
        // Desktop passes the default [0.5, 1.0] range; mobile passes a lower, capped range.
        this.quality = createAdaptiveResolution(
            this.renderer,
            this.uniforms.resolution.value,
            this.initialQuality,
            { min: this.qualityMin, max: this.qualityMax },
        );

        if (this.postEnabled) this._initPostProcess();

        window.addEventListener('resize', this._onResize);
        window.addEventListener('mousemove', this._onMouseMove);
    }

    // Build the manual post-process pipeline: the background renders into a render target, then a
    // fullscreen quad composites it to the screen through the post shader. One extra (cheap) pass.
    _initPostProcess() {
        const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
        const w = Math.max(1, size.x), h = Math.max(1, size.y);

        this.renderTarget = new THREE.WebGLRenderTarget(w, h, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            type: THREE.UnsignedByteType,
            depthBuffer: false,   // the background is a single fullscreen quad — no depth needed
            stencilBuffer: false,
        });
        this._rtSize = new THREE.Vector2(w, h); // last applied RT size (tracks the drawing buffer)
        this._dbSize = new THREE.Vector2();      // scratch for the per-frame drawing-buffer query

        this.postUniforms = {
            tDiffuse: { value: this.renderTarget.texture },
            uResolution: { value: new THREE.Vector2(w, h) },
            uNoiseTime: { value: 0.0 },
            uBlackLift: { value: this.params.blackLift },
            uAberrationPx: { value: this.params.chromaticAberrationPx },
            uNoiseAmount: { value: this.params.filmNoiseAmount },
            uNoiseSpeed: { value: POST_PROCESS.filmNoiseSpeed },
        };

        this.postMaterial = new THREE.RawShaderMaterial({
            uniforms: this.postUniforms,
            vertexShader: postProcessVertex,
            fragmentShader: postProcessFragment,
            depthTest: false,
            depthWrite: false,
        });

        this.postGeometry = new THREE.PlaneBufferGeometry(2, 2);
        this.postScene = new THREE.Scene();
        this.postMesh = new THREE.Mesh(this.postGeometry, this.postMaterial);
        this.postScene.add(this.postMesh);
    }

    // Single render entry point. Either composites through the post pass, or (post disabled) renders
    // the background straight to the screen exactly as before. Called by _animate and _renderStatic.
    _renderFrame() {
        if (!this.postEnabled) {
            this.renderer.setRenderTarget(null);
            this.renderer.render(this.scene, this.camera);
            return;
        }

        // Keep the render target matched to the (possibly adaptive) drawing-buffer size.
        this.renderer.getDrawingBufferSize(this._dbSize);
        if (this._dbSize.x !== this._rtSize.x || this._dbSize.y !== this._rtSize.y) {
            const w = Math.max(1, this._dbSize.x), h = Math.max(1, this._dbSize.y);
            this.renderTarget.setSize(w, h);
            this._rtSize.set(w, h);
            this.postUniforms.uResolution.value.set(w, h);
        }

        // Upload the live post params (per-section, tweened) + the grain time.
        // _holdGlow scales chromatic aberration up to 2× during the hold (ramps with shininess); it
        // snaps back to 0 at the burst, restoring the default aberration as the manifesto opens.
        const p = this.params, pu = this.postUniforms;
        pu.uNoiseTime.value = this._postElapsed;
        pu.uBlackLift.value = p.blackLift;
        pu.uAberrationPx.value = this._mob('chromaticAberrationPx', p.chromaticAberrationPx) * (1.0 + this._holdGlow);
        pu.uNoiseAmount.value = this._mob('filmNoiseAmount', p.filmNoiseAmount);

        // 1) background → render target
        this.renderer.setRenderTarget(this.renderTarget);
        this.renderer.render(this.scene, this.camera);
        // 2) post quad → screen (reuses the same fullscreen ortho camera)
        this.renderer.setRenderTarget(null);
        this.renderer.render(this.postScene, this.camera);
    }

    // Mouse parallax target (listener on window, not the canvas — the layer is pointer-events:none).
    // Normalized to [-1,1]; eased toward in _animate. Y is inverted (negated).
    _onMouseMove(event) {
        this.mouseTargetX = (event.clientX / window.innerWidth) * 2.0 - 1.0;
        this.mouseTargetY = -((event.clientY / window.innerHeight) * 2.0 - 1.0);
    }

    /** Begin rendering: an animation loop, or a single static frame under reduced motion.
     *  Idempotent — a second call is a no-op (guards against orphaned RAF chains). */
    start() {
        if (this.disposed || this.started) return;
        this.started = true;
        if (this.animateEnabled) {
            this.last = performance.now();
            this.rafId = requestAnimationFrame(this._animate);
        } else {
            this._renderStatic();
        }
    }

    // Scale a param value for the mobile profile (no-op on desktop, where isMobileProfile is false →
    // returns the value unchanged → byte-identical). Used at the param→uniform write sites only.
    _mob(field, value) {
        return this.isMobileProfile ? value * MOBILE_PARAM_MUL[field] : value;
    }

    // The camera origin + basis are frame-constant (they do not depend on the pixel), so they
    // are computed once per frame on the CPU and uploaded as uniforms. The origin orbits the form on a
    // sphere of radius cameraDist via two rotations in standard azimuth/elevation order: elevation
    // (Y-Z plane) FIRST, then azimuth (X-Z plane). The order matters — doing elevation first keeps the
    // vertical position = cameraDist·sin(elevation), independent of the azimuth. (The reverse order made
    // y ∝ sin(azimuth)·sin(elevation), so the vertical mouse response scaled with — and flipped sign
    // across — the horizontal position.) Each rotation is a row-vector × mat2(c,-s,s,c): u' = u·c - w·s,
    // w' = u·s + w·c. The eased mouse offset (this.mouseX/Y) is layered additively onto both angles; with
    // the mouse still it is 0, so the autonomous orbit is unchanged.
    _updateCameraUniforms() {
        const p = this.params;
        // Mobile pulls the camera back (cameraDist ×1.35) so the form reads smaller behind content;
        // desktop is unchanged (_mob is a no-op there).
        let x = 0.0, y = 0.0, z = -this._mob('cameraDist', p.cameraDist);

        // Mouse parallax is disabled on the mobile profile (no cursor on touch; synthesized mouse events
        // would otherwise jitter the form). Desktop uses the param unchanged.
        const mouseRotAmp = this.isMobileProfile ? 0.0 : p.mouseRotAmp;

        // Elevation (pitch) FIRST — rotate the Y-Z plane, so the vertical position depends only on `b` and
        // not on the azimuth `a`. mouseY is SUBTRACTED so moving the cursor up tilts the view up (the form
        // "looks toward" the cursor), consistently across the whole screen.
        const b = Math.sin(this._orbitY) - this.mouseY * mouseRotAmp;
        const cb = Math.cos(b), sb = Math.sin(b);
        let ry = y * cb - z * sb;
        let rz = y * sb + z * cb;
        y = ry; z = rz;

        // Azimuth (yaw) SECOND — rotate the X-Z plane. _orbitX (rate-integrated) replaces the old
        // `time * camRotSpeedX` so a camRotSpeed change can't retroactively swing the angle (see _animate /
        // the constructor note); _spinPhase is the inertial burst phase, 0 at rest.
        const a = this._orbitX + this._spinPhase + this._labOrbit + this.mouseX * mouseRotAmp;
        const ca = Math.cos(a), sa = Math.sin(a);
        let rx = x * ca - z * sa;
        rz = x * sa + z * ca;
        x = rx; z = rz;

        this._ro.set(x, y, z);
        this._ww.copy(this._ro).multiplyScalar(-1.0).normalize();   // normalize(ta - ro), ta = origin
        this._uu.crossVectors(this._ww, CAMERA_UP).normalize();
        this._vv.crossVectors(this._uu, this._ww).normalize();

        // Scroll-coupled vertical pan: shift the ray origin along world-Y AFTER fixing the basis, so
        // the rays keep their direction and the whole image pans (no look-tilt). _scrollSection eases
        // 0→3 with the section scroll; subtracting moves the view "with" the scroll (content rises as
        // you descend). cameraScrollShift = 0 disables it.
        this._ro.y -= this._scrollSection * this._mob('cameraScrollShift', p.cameraScrollShift);

        // Manifesto hold shake: a high-frequency tremble of the ray origin (shakes the whole image, since
        // the form is camera-raymarched). Uses the real-time clock (_postElapsed) because this.time is
        // frozen during the hold. Amplitude = _shake01 (ramps with hold progress) × shakeAmp (tunable).
        if (this._shake01 > 0.0) {
            const t = this._postElapsed, amp = this._shake01 * this._mob('shakeAmp', p.shakeAmp);
            this._ro.x += Math.sin(t * SHAKE.freqX) * amp;
            this._ro.y += Math.cos(t * SHAKE.freqY) * amp;
            this._ro.z += Math.sin(t * SHAKE.freqZ) * amp * 0.5;
        }

        const u = this.uniforms;
        u.uRo.value.copy(this._ro);
        u.uUu.value.copy(this._uu);
        u.uVv.value.copy(this._vv);
        u.uWw.value.copy(this._ww);
    }

    _animate(now) {
        if (this.disposed) return;
        this.rafId = requestAnimationFrame(this._animate);

        const dtMs = now - this.last;
        this.last = now;
        const dt = Math.min(dtMs / 1000.0, 0.1); // seconds; clamp guards against tab-restore jumps

        this.quality.tick(dtMs);

        // Intro (once, on start): fade shininess 0 -> params.shininess over INTRO_DURATION.
        this.introElapsed = Math.min(INTRO_DURATION, this.introElapsed + dt);
        const introT = this.introElapsed / INTRO_DURATION;

        const p = this.params;
        const u = this.uniforms;
        u.time.value = this.time;
        // Manifesto voice loudness — read once per frame (smoothed) and reused for shininess + timeSpeed.
        const voiceLevel = getManifestoVoiceLevel();
        // _holdGlow lerps shininess toward 1.0 during the hold (the bulb charging); 0 = the param value.
        // The voice volume adds on top (floored at the current shininess) — 0 when not playing.
        const audioGlow = voiceLevel * p.manifestoAudioSensitivity;
        // Mobile dims the resting/section glow (calmer behind content); the hold-glow ramp and the
        // manifesto voice glow (audioGlow) keep their reactive feel layered on the dimmed base.
        const baseShininess = this._mob('shininess', p.shininess);
        u.shininess.value = (baseShininess + (1.0 - baseShininess) * this._holdGlow) * introT + audioGlow;
        u.overture.value = p.baseOverture;
        u.prismSize.value = p.prismSize;
        u.uShape.value = p.uShape;
        u.uIcoComplexity.value = p.uIcoComplexity;
        u.lightColor.value.copy(this._lightColor); // per-project glow tint (gsap-tweened by setLightColor)

        // Ease the camera's mouse-parallax offset toward the current target (dt-aware).
        // _motionScale (1→0 during the manifesto hold) scales the motion INCREMENTS — not the resulting
        // angles — so the form holds its current pose as it freezes rather than snapping to angle 0.
        const mk = Math.min(1.0, p.mouseSmooth * dt * 60.0) * this._motionScale;
        this.mouseX += (this.mouseTargetX - this.mouseX) * mk;
        this.mouseY += (this.mouseTargetY - this.mouseY) * mk;

        // Integrate the inertial spin velocity into the orbit phase (rad/s × s).
        this._spinPhase += this._spinVel * dt * this._motionScale;
        // Friction coast: once the accelerator is released (_spinAccel false), the velocity decays
        // exponentially each frame instead of being tweened to 0 — natural spin-down, no brake. Frame-rate
        // independent (per-60fps-frame damping ^ dt·60). Snap to 0 only below the (tiny) cutoff.
        if (!this._spinAccel && this._spinVel !== 0) {
            this._spinVel *= Math.pow(this._spinDamping, dt * 60.0);
            if (Math.abs(this._spinVel) < this._spinCutoff) this._spinVel = 0;
        }
        // Wrap the phase to keep float precision over time — invisible (the phase feeds cos/sin, periodic in 2π).
        if (this._spinPhase > TWO_PI) this._spinPhase -= TWO_PI;
        else if (this._spinPhase < -TWO_PI) this._spinPhase += TWO_PI;

        // LAB carousel drag → horizontal rotation. Accumulate the frame-to-frame delta of the published
        // carousel offset (× labDragRotation) into _labOrbit. Delta-based so it tracks the live drag and its
        // momentum coast; gated to LAB and jump-rejected (LAB_DRAG_MAX_STEP) so filter resets / programmatic
        // re-baselining (a big one-frame jump) don't spin the form. Outside LAB the offset is static → no-op.
        const lab = getLabState();
        const labOffset = lab.carouselOffset || 0;
        const dOff = labOffset - this._prevLabOffset;
        this._prevLabOffset = labOffset;
        if (lab.active && Math.abs(dOff) <= LAB_DRAG_MAX_STEP) {
            this._labOrbit += dOff * p.labDragRotation;
            if (this._labOrbit > TWO_PI) this._labOrbit -= TWO_PI; else if (this._labOrbit < -TWO_PI) this._labOrbit += TWO_PI;
        }

        this._updateCameraUniforms();

        // Frame-rate independent advance (x60 keeps the original 60fps visual speed).
        // _motionScale freezes the form's own clock during the hold (stops the shimmer churn + orbit).
        // During the manifesto the voice drives a faster churn: timeSpeed = 0.2 (MANIFESTO_LOOK) +
        // voiceLevel × gain. Outside the manifesto voiceLevel is 0, so this is just the section timeSpeed.
        const effectiveTimeSpeed = p.timeSpeed + voiceLevel * MANIFESTO_TIMESPEED_AUDIO_GAIN;
        const dTime = effectiveTimeSpeed * dt * 60.0 * this._motionScale;
        this.time += dTime;
        if (this.time > 1000.0) this.time = 0;
        // Integrate the autonomous orbit by camRotSpeed × Δtime (the same increment as this.time, so the
        // steady-state orbit speed is unchanged — still coupled to timeSpeed). Because it's a rate, a
        // camRotSpeed change only affects the orbit forward (no retroactive swing). Wrap for float precision.
        this._orbitX += p.camRotSpeedX * dTime;
        this._orbitY += p.camRotSpeedY * dTime;
        if (this._orbitX > TWO_PI) this._orbitX -= TWO_PI; else if (this._orbitX < -TWO_PI) this._orbitX += TWO_PI;
        if (this._orbitY > TWO_PI) this._orbitY -= TWO_PI; else if (this._orbitY < -TWO_PI) this._orbitY += TWO_PI;

        // Real-time accumulator for the film grain (animates even when timeSpeed = 0 / paused).
        this._postElapsed += dt;

        this._renderFrame();
    }

    // Reduced motion: settle to the post-intro look and render a single frame (no RAF loop).
    _renderStatic() {
        const p = this.params;
        const u = this.uniforms;
        u.time.value = this.time;          // 0 on first paint — deterministic shape
        u.shininess.value = this._mob('shininess', p.shininess); // skip the fade-in; show the (mobile-calmed) settled state
        u.overture.value = p.baseOverture;
        u.prismSize.value = p.prismSize;
        u.uShape.value = p.uShape;
        u.uIcoComplexity.value = p.uIcoComplexity;
        u.lightColor.value.copy(this._lightColor); // per-project glow tint
        this._updateCameraUniforms();
        // Grain stays frozen under reduced motion (_postElapsed never advances) — no animation.
        this._renderFrame();
    }

    _onResize() {
        if (this.disposed) return;
        this.quality.onResize();
        // When paused (reduced motion) nothing re-renders on its own — refit once.
        if (!this.animateEnabled) this._renderStatic();
    }

    // ── Control hooks ─────────────────────────────────────────────────────────────────
    /** Audio / manifesto reactivity hook — stores the value; its mapping is deferred (currently inert). */
    setIntensity(value) { this.intensity = value; }

    /**
     * Tint the central glow toward `hex` (e.g. the shown PROJECTS item's color). Smoothly tweens the
     * light color when animating; snaps + repaints under reduced motion. The value persists (nothing
     * resets it on section/route change) until the next call or a reload. `duration: 0` snaps (used to
     * seed the engine from the persisted color on mount).
     * @param {string|number} hex CSS color string ('#RRGGBB') or hex number.
     * @param {number} [duration=0.5] tween seconds; 0 = snap.
     */
    setLightColor(hex, duration = 0.5) {
        if (this.disposed || !this.params) return;
        const target = new THREE.Color(hex);
        // Brightness-preserving tint: the workList colors are dark, and a straight multiply would dim the
        // glow as much as hue it. Normalize to the brightest channel (max → 1) so we apply the project's
        // HUE at full luminance — the glow's brightness stays owned by shininess, not the color's darkness.
        // White stays white (neutral no-op); pure black is left as-is (guarded) but no project uses it.
        const m = Math.max(target.r, target.g, target.b);
        if (m > 0) target.setRGB(target.r / m, target.g / m, target.b / m);
        gsap.killTweensOf(this._lightColor);
        if (!this.animateEnabled || duration === 0) {
            this._lightColor.copy(target);
            if (!this.animateEnabled) this._renderStatic(); // no RAF loop — repaint the single frame
        } else {
            gsap.to(this._lightColor, { r: target.r, g: target.g, b: target.b, duration, ease: 'power2.inOut' });
        }
    }

    /**
     * Move the look to the preset for the given home section, with scroll-driven transition
     * choreography (see _buildTransition / transitionConfig.js) instead of one uniform crossfade.
     * @param {0|1|2|3|null} section Home section index (null = home route unmounted).
     */
    setSection(section) {
        if (this.disposed || !this.params) return;

        // 1) Vertical scroll pan — tracks the section INDEX (so it eases even within a shared key).
        //    null (off home route) → 0, so the camera recenters. Tweened in lockstep with the scroll.
        const idx = (section == null) ? 0 : section;
        if (this._scrollTween) { this._scrollTween.kill(); this._scrollTween = null; }
        if (this.animateEnabled) {
            this._scrollTween = gsap.to(this, { ...SECTION_TWEEN, _scrollSection: idx });
            // Mobile: run the camera scroll pan faster (shorter passage on touch).
            if (this.isMobileProfile) this._scrollTween.timeScale(MOBILE_SECTION_TIMESCALE);
        } else {
            this._scrollSection = idx;
        }

        // 2) Look choreography — deduped by preset key (no redundant re-run on repeat publishes).
        const key = presetKeyForSection(section);
        if (key === this._sectionKey) {
            if (!this.animateEnabled) this._renderStatic();
            return;
        }
        const fromKey = this._sectionKey;
        this._sectionKey = key;
        const target = SECTION_PRESETS[key];

        // Kill the in-flight look timeline. Clear _spinAccel too: if a spin-up tween was mid-flight its
        // onComplete won't fire, so reset the flag here — the frame-friction decay then coasts whatever
        // velocity remains (no stranded "always accelerating" state, no hard zeroing).
        if (this._lookTween) { this._lookTween.kill(); this._lookTween = null; }
        this._spinAccel = false;

        if (!this.animateEnabled) {
            // Reduced motion: snap to the preset, no spin, single repaint (keeps it calm).
            Object.assign(this.params, target);
            this._spinVel = 0; this._spinPhase = 0; this.activeTransition = null;
            this._renderStatic();
            return;
        }

        this._lookTween = this._buildTransition(fromKey, key, target);
        // Mobile: speed up the whole look choreography (param settle + open/close + spin ramp)
        // so the transition reads as a short rupture rather than a long passage.
        if (this.isMobileProfile && this._lookTween) this._lookTween.timeScale(MOBILE_SECTION_TIMESCALE);
    }

    /**
     * Add the spin RAMP to a transition timeline: a smooth GSAP tween of _spinVel up to the peak (an
     * S-curve, no instant jump). _spinAccel is held true for the ramp's duration so the frame-friction
     * decay in _animate is suspended; when the ramp completes _spinAccel flips false and the velocity
     * COASTS DOWN under friction (no second tween, no hard zeroing). Also latches this transition's
     * damping/cutoff so the coast (which outlives the timeline) uses the right friction.
     */
    _addSpinRamp(tl, c) {
        this._spinDamping = c.spinDamping;
        this._spinCutoff = c.minSpinVelocityCutoff;
        tl.to(this, {
            duration: c.spinAccelerationDuration,
            ease: c.spinAccelEase,
            _spinVel: c.maxSpinSpeed * (this.isMobileProfile ? MOBILE_SPIN_SCALE : 1),
            onStart: () => { this._spinAccel = true; },      // hold off friction while accelerating
            onComplete: () => { this._spinAccel = false; },  // release accelerator → friction coast
        }, 0);
    }

    /**
     * Build the GSAP timeline that moves the look from one section preset to another. Owns the param
     * tweens; the spin is velocity/inertia based (ramp tween + frame friction, see _addSpinRamp /
     * _animate) so it never fights a tween. Sets this.activeTransition for the run (cleared onComplete).
     *
     * Choreography by (fromKey → toKey):
     *   home → opened  "heroToProjects": spin ramps up smoothly → opens after a delay while spinning →
     *                  spin COASTS DOWN under friction. The closed emblem ruptures open. (Any Hero→opened.)
     *   deep → home    "projectsToHero": the mirror of the rupture (scroll-up) — spin ramps up, the form
     *                  CLOSES while spinning, then the spin coasts down. Fully visible (no loop mask).
     *   about → home   "aboutToHero": spin ramps up + close (overture → home) + pull back/recenter, then
     *                  the spin coasts down into Hero idle. Fires on the bottom-scroll loop / About→Hero nav.
     *   lab → about    "labToAbout": stays open; the camera GLIDES closer (cameraDist tweened separately
     *                  with a strong inertial ease over a longer duration) while brightness/motion fade.
     *   deep → lab     "projectsToLab": a weighted settle into the deeper LAB framing (cameraDist 85→95,
     *                  timeSpeed 0.01→0.008) — a gentle descent, no spin/open event.
     *   else           default settle (reverse nav, menu jumps). Residual spin coasts via frame friction.
     */
    _buildTransition(fromKey, toKey, target) {
        const tl = gsap.timeline({ onComplete: () => { this.activeTransition = null; } });

        // Hero (closed) → any opened section: the rupture (ramp up, open while spinning, coast down).
        if (fromKey === 'home' && toKey !== 'home') {
            const c = SECTION_TRANSITIONS.heroToProjects;
            const { baseOverture, ...rest } = target;
            this.activeTransition = 'heroToProjects';
            this._addSpinRamp(tl, c);
            // everything except the opening settles from the start; the opening waits openDelay
            tl.to(this.params, { duration: c.paramDuration, ease: c.paramEase, ...rest }, 0);
            tl.to(this.params, { duration: c.openDuration, ease: c.openEase, baseOverture }, c.openDelay);
            return tl;
        }

        // About (opened, close-up) → Hero (closed, centered): the reconstitution.
        if (toKey === 'home' && fromKey === 'about') {
            const c = SECTION_TRANSITIONS.aboutToHero;
            const { baseOverture, ...rest } = target; // target = home preset
            this.activeTransition = 'aboutToHero';
            this._addSpinRamp(tl, c);
            tl.to(this.params, { duration: c.recenterDuration, ease: c.recenterEase, ...rest }, 0); // pull back + recenter
            tl.to(this.params, { duration: c.closeDuration, ease: c.closeEase, baseOverture }, 0);   // close the form
            return tl;
        }

        // Lab → About: weighted, inertial camera glide. Tween cameraDist on its own strong ease/duration
        // (accelerate in → decelerate softly into the close-up) so it reads as approach, not a slide; the
        // rest (brightness, motion speed, orbit) fades down gradually. The form stays open (overture held).
        if (fromKey === 'lab' && toKey === 'about') {
            const c = SECTION_TRANSITIONS.labToAbout;
            const { cameraDist, ...rest } = target;
            this.activeTransition = 'labToAbout';
            tl.to(this.params, { duration: c.cameraApproachDuration, ease: c.cameraEase, cameraDist }, 0);
            tl.to(this.params, { duration: c.duration, ease: c.ease, ...rest }, 0);
            return tl;
        }

        // Projects (opened) → Hero (closed): the mirror of the rupture — spin up, close while spinning, coast down.
        if (fromKey === 'deep' && toKey === 'home') {
            const c = SECTION_TRANSITIONS.projectsToHero;
            const { baseOverture, ...rest } = target; // target = home preset (closed)
            this.activeTransition = 'projectsToHero';
            this._addSpinRamp(tl, c);
            // everything except the closing settles from the start; the close waits closeDelay
            tl.to(this.params, { duration: c.paramDuration, ease: c.paramEase, ...rest }, 0);
            tl.to(this.params, { duration: c.closeDuration, ease: c.closeEase, baseOverture }, c.closeDelay);
            return tl;
        }

        // Projects → Lab: a weighted settle into the deeper LAB framing (lab preset now diverges from deep
        // in cameraDist/timeSpeed). Else: default crossfade (reverse nav, menu jumps). Any residual burst
        // spin coasts down via frame friction (_spinAccel is false) — no hard zeroing tween here, so an
        // interrupted spin glides out instead of braking.
        let timing = SECTION_TRANSITION_DEFAULT;
        if (fromKey === 'deep' && toKey === 'lab') { timing = SECTION_TRANSITIONS.projectsToLab; this.activeTransition = 'projectsToLab'; }
        else { this.activeTransition = 'section'; }

        tl.to(this.params, { duration: timing.duration, ease: timing.ease, ...target }, 0);
        return tl;
    }

    /**
     * Manifesto hold (0..1) freeze + shake. Driven frame-accurately by HomeHero's hold progress via the
     * manifestoBackground pub/sub. A number freezes motion (_motionScale = 1 - p) and ramps the shake
     * (_shake01 = p), holding the form's current pose. `null` releases (hold aborted) — eases both back to
     * live motion / no shake. Completion does NOT release here; setManifestoActive(true) takes over.
     * @param {number|null} progress 0..1 during the hold, or null on release/abort.
     */
    setHold(progress) {
        if (this.disposed || !this.params) return;
        gsap.killTweensOf(this, '_motionScale,_shake01,_holdGlow');
        if (progress == null) {
            if (this.animateEnabled) {
                gsap.to(this, { _motionScale: 1.0, _shake01: 0.0, _holdGlow: 0.0, duration: HOLD_RELEASE.duration, ease: HOLD_RELEASE.ease });
            } else {
                this._motionScale = 1.0; this._shake01 = 0.0; this._holdGlow = 0.0;
            }
            return;
        }
        const pr = Math.max(0.0, Math.min(1.0, progress));
        this._motionScale = 1.0 - pr; // freeze proportional to the hold
        this._shake01 = pr;           // shake ramps in
        this._holdGlow = pr;          // shininess ramps toward 1.0 (the bulb charging)
    }

    /**
     * Manifesto activation. On true, the form EXPLODES to MANIFESTO_LOOK (central sphere appears at
     * prismSize 6) while motion returns (_motionScale→1) and the shake stops (_shake01→0). On false it
     * returns to the active section preset. Reuses the _lookTween slot so setSection() coordinates with
     * it (a section change kills this tween and tweens to the destination preset). Wired from
     * Home.activate/deactivateManifestoFromHero via the manifestoBackground pub/sub.
     * @param {boolean} active true → manifesto look, false → section default.
     */
    setManifestoActive(active) {
        if (this.disposed || !this.params) return;
        this._manifestoActive = active;

        // The explosion brings motion back / ends the shake + hold-glow regardless of where the hold left them.
        gsap.killTweensOf(this, '_motionScale,_shake01,_holdGlow');
        const look = active ? MANIFESTO_LOOK : SECTION_PRESETS[this._sectionKey];

        if (this._lookTween) { this._lookTween.kill(); this._lookTween = null; }

        if (this.animateEnabled) {
            // _motionScale / _shake01 ease over the burst; _holdGlow is snapped (below) so shininess is immediate.
            gsap.to(this, { _motionScale: 1.0, _shake01: 0.0, duration: MANIFESTO_EXPLODE.duration, ease: MANIFESTO_EXPLODE.ease });
            if (active) {
                // shininess snaps IMMEDIATELY to the manifesto value (the built-up hold glow releases at
                // the burst) — clear _holdGlow now and set the param directly; the rest opens over the tween.
                this._holdGlow = 0.0;
                this.params.shininess = MANIFESTO_LOOK.shininess;
                const explodeLook = { ...MANIFESTO_LOOK };
                delete explodeLook.shininess;
                this._lookTween = gsap.to(this.params, { duration: MANIFESTO_EXPLODE.duration, ease: MANIFESTO_EXPLODE.ease, ...explodeLook });
            } else {
                // Deactivation: collapse (gentler/longer) so the sphere recedes + the form recomposes
                // into the section preset, rather than snapping back on the sharp explosion ease.
                this._lookTween = gsap.to(this.params, { duration: MANIFESTO_COLLAPSE.duration, ease: MANIFESTO_COLLAPSE.ease, ...look });
            }
        } else {
            this._motionScale = 1.0; this._shake01 = 0.0; this._holdGlow = 0.0;
            Object.assign(this.params, look);
            this._renderStatic();
        }
    }

    /** Cancel the loop, drop listeners, and release all WebGL resources. */
    dispose() {
        this.disposed = true;
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this._lookTween) { this._lookTween.kill(); this._lookTween = null; }
        if (this._scrollTween) { this._scrollTween.kill(); this._scrollTween = null; }
        if (this._manifestoTween) { this._manifestoTween.kill(); this._manifestoTween = null; }
        this._spinVel = 0; this._spinAccel = false; this.activeTransition = null;
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('mousemove', this._onMouseMove);

        if (this.scene && this.mesh) this.scene.remove(this.mesh);
        if (this.geometry) this.geometry.dispose();
        if (this.material) this.material.dispose();

        // Post-process resources.
        if (this.postScene && this.postMesh) this.postScene.remove(this.postMesh);
        if (this.postGeometry) this.postGeometry.dispose();
        if (this.postMaterial) this.postMaterial.dispose();
        if (this.renderTarget) this.renderTarget.dispose();

        if (this.renderer) {
            this.renderer.setRenderTarget(null);
            this.renderer.dispose();
            if (this.renderer.forceContextLoss) this.renderer.forceContextLoss();
            const canvas = this.renderer.domElement;
            if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
        }

        this.renderer = null;
        this.material = null;
        this.geometry = null;
        this.mesh = null;
        this.scene = null;
        this.camera = null;
        this.quality = null;
        this.uniforms = null;
        this.postMaterial = null;
        this.postGeometry = null;
        this.postMesh = null;
        this.postScene = null;
        this.renderTarget = null;
        this.postUniforms = null;
    }
}
